import {Select, SelectProps} from 'antd';
import {FC, useMemo, useState} from 'react';
import {DEFAULT_API_NAME, useApi} from '../../util/Auth';
import {SeasonRecord} from '../table/Season';

const singularRoute = '/season';
const pluralRoute = '/seasons';

export interface SeasonSelectProps extends SelectProps<string> {
  onSeasonChange?: (season?: SeasonRecord) => void;
}

export const SeasonSelect: FC<SeasonSelectProps> = ({
  onSeasonChange,
  ...props
}) => {
  const api = useApi();
  const [data, setData] = useState<SeasonRecord[]>([]);
  const [value, setValue] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [timeoutId, setTimeoutId] = useState<
    ReturnType<typeof setTimeout> | undefined
  >();

  const {value: initialValue} = props;
  const [initialFetchedValue, setInitialFetchedValue] = useState<string>();

  useMemo(() => {
    if (!api) return;
    if (!initialValue || value || loading) return;
    if (initialFetchedValue === initialValue) return;
    setInitialFetchedValue(initialValue);
    setLoading(true);

    (async () => {
      try {
        const season = await api.get(
          DEFAULT_API_NAME,
          `${singularRoute}/${initialValue}`,
          {},
        );

        if (!season) return;

        setData([season]);
        setValue(season.id);
        onSeasonChange?.(season);
      } finally {
        setLoading(false);
      }
    })();
  }, [api, initialValue, value, loading, initialFetchedValue, onSeasonChange]);

  const fetch = (value: string, callback: Function) => {
    if (!api) return;

    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(undefined);
    }

    const req = async () => {
      setLoading(true);
      try {
        const res = await api.get(DEFAULT_API_NAME, pluralRoute, {
          queryStringParameters: {
            limit: 10,
            f_name: value,
            sortKey: 'code',
            order: 'ASC',
          },
        });

        callback(res.entities);
      } finally {
        setLoading(false);
      }
    };

    setTimeoutId(setTimeout(req, 300));
  };

  const handleSearch = (id: string) => {
    if (id) {
      fetch(id, setData);
    } else {
      setData([]);
    }
  };

  const handleChange: SelectProps<string>['onChange'] = (id, option) => {
    setValue(id);
    const season = data?.find((season) => season.id === id);
    if (season) {
      onSeasonChange?.(season);
    }
    props.onChange?.(id, option);
  };

  return (
    <Select<string>
      {...props}
      showSearch
      defaultActiveFirstOption={false}
      showArrow={false}
      filterOption={false}
      onSearch={handleSearch}
      onChange={handleChange}
      notFoundContent={null}
      options={(data || []).map((d) => ({
        value: d.id,
        label: d.code,
      }))}
      value={value}
      loading={loading || props.loading}
    />
  );
};
